import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {AUTH_INIT_MODELS, SHORT_LINK_TYPE} from '@chekin/core';
import {useChekinSDK} from 'context/ChekinSDK';
import {useUIVariant} from 'hooks/useUIVariant';
import {isTerminalSession} from 'hooks/terminalMode';
import {PATHS} from 'routes/constants';
import {UI_VARIANTS} from 'utils/uiVariant';
import {useInstantCheckInOffer} from 'pages/InstantCheckIn/useInstantCheckInOffer';

export type EntranceKind = 'property' | 'reservation' | 'instant';
type EntranceDecision = EntranceKind | 'skip';

function resolveKind({
  linkType,
  modelName,
  pathname,
  foundReservationId,
  shouldOfferInstant,
}: {
  linkType?: string;
  modelName?: string;
  pathname: string;
  foundReservationId?: string;
  shouldOfferInstant: boolean;
}): EntranceDecision | null {
  const path = pathname.split('/').filter(Boolean).slice(1).join('/');
  const isSearchRoute = path.startsWith(PATHS.searchReservation.main);
  const isReservationModel = modelName === AUTH_INIT_MODELS.reservation;
  const route = isSearchRoute && isReservationModel ? '' : path;

  if (
    linkType === SHORT_LINK_TYPE.housingCheckinOnline ||
    (isSearchRoute && !isReservationModel)
  ) {
    return 'property';
  }

  const isReservationLink = !linkType || linkType === SHORT_LINK_TYPE.main;
  const isAuthRoute = route.split('/')[0] === PATHS.auth.root;
  if (isReservationLink && isAuthRoute) {
    return 'instant';
  }

  if (isReservationLink && foundReservationId) {
    if (route) {
      return 'skip';
    }
    return shouldOfferInstant ? 'instant' : 'reservation';
  }

  return null;
}

const UNDECIDED_GRACE_MS = 150;

function useEntranceKind() {
  const {isSuccessSDK, linkType, modelName, foundReservationId, hasToken} =
    useChekinSDK();
  const {variant, isResolved} = useUIVariant();
  const {pathname} = useLocation();
  const instantOffer = useInstantCheckInOffer();
  const [lockedKind, setLockedKind] = useState<EntranceDecision | null | undefined>();

  const isDecidable =
    hasToken &&
    isSuccessSDK &&
    isResolved &&
    (!foundReservationId || instantOffer.isDecided);
  const kind =
    isDecidable && variant === UI_VARIANTS.redesign && !isTerminalSession()
      ? resolveKind({
          linkType,
          modelName,
          pathname,
          foundReservationId,
          shouldOfferInstant: instantOffer.shouldOffer,
        })
      : null;

  useEffect(
    function lockKindOnce() {
      if (lockedKind !== undefined || !isDecidable) {
        return;
      }

      if (kind) {
        setLockedKind(kind);
        return;
      }

      const timeout = setTimeout(() => setLockedKind(null), UNDECIDED_GRACE_MS);
      return () => clearTimeout(timeout);
    },
    [isDecidable, kind, lockedKind],
  );

  const isSettled =
    lockedKind !== undefined ||
    !hasToken ||
    (isSuccessSDK &&
      isResolved &&
      (variant !== UI_VARIANTS.redesign || isTerminalSession()));

  const isSkipped = lockedKind === 'skip';
  return {kind: isSkipped ? null : lockedKind ?? null, isSkipped, isSettled};
}

export {resolveKind, useEntranceKind};
